import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Edit, FileDown, Paperclip } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (v) => (v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtDate = (d) => d ? d.split('-').reverse().join('/') : '-';

const emptyForm = {
  supplier_id: '', numero_factura_proveedor: '', fecha_factura: new Date().toISOString().slice(0,10),
  fecha_recepcion: new Date().toISOString().slice(0,10), concepto: '', categoria: '',
  base_imponible: '', iva_porcentaje: 21, retencion_porcentaje: 0,
  estado_pago: 'pendiente', fecha_pago: '', forma_pago: 'Transferencia', notas: '', archivo: null,
};

const Expenses = () => {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      setLoading(true);
      const [e, s] = await Promise.all([
        axios.get(`${API_URL}/billing/expenses`),
        axios.get(`${API_URL}/billing/suppliers`),
      ]);
      setItems(e.data); setSuppliers(s.data);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (g) => {
    setEditing(g);
    setForm({
      supplier_id: g.supplier_id || '', numero_factura_proveedor: g.numero_factura_proveedor,
      fecha_factura: g.fecha_factura, fecha_recepcion: g.fecha_recepcion || '',
      concepto: g.concepto, categoria: g.categoria || '',
      base_imponible: g.base_imponible, iva_porcentaje: g.iva_porcentaje,
      retencion_porcentaje: g.retencion_porcentaje, estado_pago: g.estado_pago,
      fecha_pago: g.fecha_pago || '', forma_pago: g.forma_pago, notas: g.notas || '',
      archivo: null,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.archivo;
        Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
        payload.base_imponible = parseFloat(payload.base_imponible);
        payload.iva_porcentaje = parseFloat(payload.iva_porcentaje);
        payload.retencion_porcentaje = parseFloat(payload.retencion_porcentaje);
        await axios.put(`${API_URL}/billing/expenses/${editing.id}`, payload);
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '') return;
          if (k === 'archivo' && v) fd.append('archivo', v);
          else if (k !== 'archivo') fd.append(k, v);
        });
        await axios.post(`${API_URL}/billing/expenses`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(editing ? 'Gasto actualizado' : 'Gasto registrado');
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try { await axios.delete(`${API_URL}/billing/expenses/${id}`); toast.success('Eliminado'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openFile = (url) => {
    if (!url) return;
    const token = localStorage.getItem('token');
    const fname = url.split('/').pop();
    axios.get(`${API_URL}/billing/expenses/file/${fname}`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } })
      .then(r => { const b = new Blob([r.data]); window.open(URL.createObjectURL(b), '_blank'); })
      .catch(() => toast.error('No se pudo abrir el fichero'));
  };

  const filtered = items.filter(g => {
    if (filterEstado && g.estado_pago !== filterEstado) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return g.concepto?.toLowerCase().includes(s) ||
           g.numero_factura_proveedor?.toLowerCase().includes(s) ||
           g.numero_interno?.toLowerCase().includes(s) ||
           g.supplier_data?.nombre_fiscal?.toLowerCase().includes(s);
  });

  const totalBase = filtered.reduce((s, g) => s + (g.base_imponible || 0), 0);
  const totalIva = filtered.reduce((s, g) => s + (g.total_iva || 0), 0);
  const totalPagado = filtered.filter(g => g.estado_pago === 'pagada').reduce((s,g)=> s + (g.total||0), 0);
  const totalPendiente = filtered.filter(g => g.estado_pago !== 'pagada').reduce((s,g)=> s + (g.total||0), 0);

  const estadoColor = { pendiente: 'bg-amber-100 text-amber-800', parcial: 'bg-blue-100 text-blue-800', pagada: 'bg-green-100 text-green-800' };

  return (
    <Layout>
      <div className="space-y-6" data-testid="expenses-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">Gastos</h1>
            <p className="text-slate-500 mt-1">Facturas y recibos recibidos ({items.length})</p>
          </div>
          <Button onClick={openCreate} data-testid="new-expense-button" className="bg-[#004E89] hover:bg-[#003a68]">
            <Plus className="w-4 h-4 mr-2" /> Nuevo gasto
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">Base imponible</div><div className="text-lg font-bold">{money(totalBase)}</div></div>
          <div className="bg-white border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">IVA soportado</div><div className="text-lg font-bold">{money(totalIva)}</div></div>
          <div className="bg-white border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">Pagado</div><div className="text-lg font-bold text-green-700">{money(totalPagado)}</div></div>
          <div className="bg-white border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">Pendiente</div><div className="text-lg font-bold text-red-700">{money(totalPendiente)}</div></div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Buscar concepto, nº factura, proveedor..." value={search} onChange={e=>setSearch(e.target.value)} />
          </div>
          <select value={filterEstado} onChange={e=>setFilterEstado(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
            <option value="">Todos los estados</option><option value="pendiente">Pendiente</option><option value="parcial">Parcial</option><option value="pagada">Pagada</option>
          </select>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? <div className="p-12 text-center text-slate-500">Cargando...</div>
            : filtered.length === 0 ? <div className="p-12 text-center text-slate-500">Sin gastos.</div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Interno</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Factura prov.</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Fecha</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Proveedor</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Concepto</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Base</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Total</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Estado</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(g => (
                      <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 text-xs font-mono text-slate-500">{g.numero_interno}</td>
                        <td className="p-3 text-sm font-medium">{g.numero_factura_proveedor}</td>
                        <td className="p-3 text-sm">{fmtDate(g.fecha_factura)}</td>
                        <td className="p-3 text-sm">{g.supplier_data?.nombre_fiscal || '-'}</td>
                        <td className="p-3 text-sm text-slate-700 truncate max-w-xs">{g.concepto}<div className="text-xs text-slate-500">{g.categoria}</div></td>
                        <td className="p-3 text-sm text-right">{money(g.base_imponible)}</td>
                        <td className="p-3 text-sm text-right font-medium">{money(g.total)}</td>
                        <td className="p-3"><span className={`inline-block text-xs px-2 py-1 rounded-full capitalize ${estadoColor[g.estado_pago]}`}>{g.estado_pago}</span></td>
                        <td className="p-3 text-right space-x-1">
                          {g.archivo_url && <button onClick={()=>openFile(g.archivo_url)} title="Ver adjunto" className="p-1 text-slate-600 hover:text-blue-600"><FileDown className="w-4 h-4 inline" /></button>}
                          <button onClick={()=>openEdit(g)} className="p-1 text-slate-600 hover:text-blue-600"><Edit className="w-4 h-4 inline" /></button>
                          <button onClick={()=>handleDelete(g.id)} className="p-1 text-slate-600 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Editar gasto' : 'Nuevo gasto'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Proveedor</Label>
                  <select value={form.supplier_id} onChange={e=>set('supplier_id', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option value="">— Sin proveedor / manual —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.nombre_fiscal} ({s.cif_nif})</option>)}
                  </select>
                </div>
                <div><Label>Nº factura proveedor *</Label><Input required value={form.numero_factura_proveedor} onChange={e=>set('numero_factura_proveedor', e.target.value)} /></div>
                <div><Label>Fecha factura *</Label><Input required type="date" value={form.fecha_factura} onChange={e=>set('fecha_factura', e.target.value)} /></div>
                <div><Label>Fecha recepción</Label><Input type="date" value={form.fecha_recepcion} onChange={e=>set('fecha_recepcion', e.target.value)} /></div>
                <div><Label>Categoría</Label><Input value={form.categoria} onChange={e=>set('categoria', e.target.value)} placeholder="Material, Suministros..." /></div>
                <div className="md:col-span-2"><Label>Concepto *</Label><Input required value={form.concepto} onChange={e=>set('concepto', e.target.value)} /></div>
                <div><Label>Base imponible (€) *</Label><Input required type="number" step="0.01" value={form.base_imponible} onChange={e=>set('base_imponible', e.target.value)} /></div>
                <div><Label>IVA (%)</Label>
                  <select value={form.iva_porcentaje} onChange={e=>set('iva_porcentaje', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option value={0}>0%</option><option value={4}>4%</option><option value={10}>10%</option><option value={21}>21%</option>
                  </select>
                </div>
                <div><Label>Retención (%)</Label><Input type="number" step="0.01" value={form.retencion_porcentaje} onChange={e=>set('retencion_porcentaje', e.target.value)} /></div>
                <div><Label>Estado pago</Label>
                  <select value={form.estado_pago} onChange={e=>set('estado_pago', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option value="pendiente">Pendiente</option><option value="parcial">Parcial</option><option value="pagada">Pagada</option>
                  </select>
                </div>
                <div><Label>Fecha pago</Label><Input type="date" value={form.fecha_pago} onChange={e=>set('fecha_pago', e.target.value)} /></div>
                <div><Label>Forma de pago</Label>
                  <select value={form.forma_pago} onChange={e=>set('forma_pago', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option>Transferencia</option><option>Domiciliación</option><option>Efectivo</option><option>Tarjeta</option><option>Otro</option>
                  </select>
                </div>
                <div className="md:col-span-2"><Label>Notas</Label><textarea className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={2} value={form.notas} onChange={e=>set('notas', e.target.value)} /></div>
                {!editing && (
                  <div className="md:col-span-2">
                    <Label>Adjuntar factura/recibo (PDF, imagen)</Label>
                    <Input type="file" accept="application/pdf,image/*" onChange={e=>set('archivo', e.target.files[0])} />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={()=>setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#004E89] hover:bg-[#003a68]">{editing ? 'Guardar' : 'Registrar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Expenses;
